import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, Users, AlertTriangle, CheckCircle, Clock, Ban,
  Calendar, Euro, FileText, Download, Loader2, RefreshCw,
  CircleDollarSign, BarChart3, PieChart, AlertCircle, History,
  FileSpreadsheet, Filter, ArrowUpRight, ArrowDownRight, Crown, RotateCcw,
  ShieldAlert, Send, X
} from 'lucide-react';
import { api } from '../context/AuthContext';
import { useAuth } from '../context/AuthContext';
import AdminVendorFinancePanel from './AdminVendorFinancePanel';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const SuperAdminAnalytics = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [kpi, setKpi] = useState(null);
  const [breakdown, setBreakdown] = useState([]);
  const [audit, setAudit] = useState(null);
  const [vendorsHistory, setVendorsHistory] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorOrders, setVendorOrders] = useState(null);
  const [subscriptions, setSubscriptions] = useState(null);
  const [subscriptionsSummary, setSubscriptionsSummary] = useState(null);
  const [activeTab, setActiveTab] = useState('kpi');

  // Ledger tab state
  const [ledgerProducers, setLedgerProducers] = useState([]);
  const [ledgerProducerId, setLedgerProducerId] = useState('');
  const [ledgerProducerName, setLedgerProducerName] = useState('');
  const [ledgerProducersLoading, setLedgerProducersLoading] = useState(false);

  // Ledger health (audit)
  const [ledgerHealth, setLedgerHealth] = useState(null);
  const [ledgerHealthLoading, setLedgerHealthLoading] = useState(false);

  // Disputes state
  const [disputes, setDisputes] = useState([]);
  const [disputesLoading, setDisputesLoading] = useState(false);
  const [disputeModal, setDisputeModal] = useState(null); // { stripe_dispute_id, order_id }
  const [evidenceForm, setEvidenceForm] = useState({
    customer_communication: '',
    product_description: '',
    shipping_carrier: '',
    shipping_tracking_number: '',
  });
  const [evidenceSubmitting, setEvidenceSubmitting] = useState(false);
  
  // Filters
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [groupBy, setGroupBy] = useState('vendor');
  const [exporting, setExporting] = useState(false);

  // Check permissions
  useEffect(() => {
    if (user && user.role !== 'super_admin') {
      navigate('/');
      toast.error('Accesso riservato ai Super Admin');
    }
  }, [user, navigate]);

  // Load data
  useEffect(() => {
    if (activeTab === 'kpi') {
      fetchKPI();
    } else if (activeTab === 'breakdown') {
      fetchBreakdown();
    } else if (activeTab === 'audit') {
      fetchAudit();
      fetchLedgerHealth();
    } else if (activeTab === 'history') {
      fetchVendorsHistory();
    } else if (activeTab === 'subscriptions') {
      fetchSubscriptions();
    } else if (activeTab === 'disputes') {
      fetchDisputes();
    } else if (activeTab === 'ledger') {
      fetchLedgerProducers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, periodStart, periodEnd, groupBy]);

  const fetchKPI = async () => {
    setLoading(true);
    try {
      const params = {};
      if (periodStart) params.period_start = periodStart;
      if (periodEnd) params.period_end = periodEnd;
      
      const res = await api.get('/admin/payments/analytics/kpi', { params });
      setKpi(res.data.data);
    } catch (error) {
      console.error('Error fetching KPI:', error);
      toast.error('Errore nel caricamento KPI');
    } finally {
      setLoading(false);
    }
  };

  const fetchBreakdown = async () => {
    setLoading(true);
    try {
      const params = { group_by: groupBy };
      if (periodStart) params.period_start = periodStart;
      if (periodEnd) params.period_end = periodEnd;
      
      const res = await api.get('/admin/payments/analytics/breakdown', { params });
      setBreakdown(res.data.data.breakdown);
    } catch (error) {
      console.error('Error fetching breakdown:', error);
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const fetchAudit = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/payments/analytics/audit', { params: { days_threshold: 15 } });
      setAudit(res.data.data);
    } catch (error) {
      console.error('Error fetching audit:', error);
      toast.error('Errore nel caricamento audit');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendorsHistory = async () => {
    setLoading(true);
    try {
      const params = {};
      if (periodStart) params.period_start = periodStart;
      if (periodEnd) params.period_end = periodEnd;
      
      const res = await api.get('/admin/payments/vendors/history', { params });
      setVendorsHistory(res.data.data);
    } catch (error) {
      console.error('Error fetching vendors history:', error);
      toast.error('Errore nel caricamento storico venditori');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const [listRes, summaryRes] = await Promise.all([
        api.get('/subscriptions/admin/list'),
        api.get('/subscriptions/admin/summary')
      ]);
      setSubscriptions(listRes.data.data);
      setSubscriptionsSummary(summaryRes.data.data);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      toast.error('Errore nel caricamento abbonamenti');
    } finally {
      setLoading(false);
    }
  };

  const fetchDisputes = async () => {
    setDisputesLoading(true);
    try {
      const res = await api.get('/admin/disputes');
      setDisputes(res.data.disputes || []);
    } catch (error) {
      console.error('Error fetching disputes:', error);
      toast.error('Errore nel caricamento dispute');
    } finally {
      setDisputesLoading(false);
    }
  };

  const openEvidenceModal = (dispute) => {
    setDisputeModal(dispute);
    setEvidenceForm({
      customer_communication: '',
      product_description: '',
      shipping_carrier: '',
      shipping_tracking_number: '',
    });
  };

  const fetchLedgerHealth = async () => {
    if (ledgerHealth !== null) return; // già caricato
    setLedgerHealthLoading(true);
    try {
      const res = await api.get('/admin/vendors/ledger-health');
      setLedgerHealth(res.data);
    } catch {
      // silenzioso — non critico per il tab audit
    } finally {
      setLedgerHealthLoading(false);
    }
  };

  const goToLedgerTab = (producerId, producerName) => {
    setLedgerProducerId(producerId);
    setLedgerProducerName(producerName);
    setActiveTab('ledger');
    fetchLedgerProducers();
  };

  const fetchLedgerProducers = async () => {
    if (ledgerProducers.length > 0) return; // già caricati
    setLedgerProducersLoading(true);
    try {
      const res = await api.get('/producers?page_size=50');
      setLedgerProducers(res.data.items || []);
    } catch {
      toast.error('Errore nel caricamento dei produttori');
    } finally {
      setLedgerProducersLoading(false);
    }
  };

  const closeEvidenceModal = () => {
    setDisputeModal(null);
    setEvidenceForm({
      customer_communication: '',
      product_description: '',
      shipping_carrier: '',
      shipping_tracking_number: '',
    });
  };

  const submitEvidence = async () => {
    if (!disputeModal) return;
    const body = Object.fromEntries(
      Object.entries(evidenceForm).filter(([, v]) => v.trim() !== '')
    );
    setEvidenceSubmitting(true);
    try {
      await api.post(`/admin/disputes/${disputeModal.stripe_dispute_id}/submit-evidence`, body);
      toast.success('Evidence submitted to Stripe');
      closeEvidenceModal();
      fetchDisputes();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Errore invio evidence';
      toast.error(msg);
    } finally {
      setEvidenceSubmitting(false);
    }
  };

  const fetchVendorOrders = async (vendorId) => {
    try {
      const params = { limit: 50 };
      if (periodStart) params.period_start = periodStart;
      if (periodEnd) params.period_end = periodEnd;
      
      const res = await api.get(`/admin/payments/vendors/${vendorId}/orders-detail`, { params });
      setVendorOrders(res.data.data);
    } catch (error) {
      console.error('Error fetching vendor orders:', error);
      toast.error('Errore nel caricamento ordini');
    }
  };

  const handleExport = async (exportType) => {
    setExporting(true);
    try {
      const params = { export_type: exportType };
      if (periodStart) params.period_start = periodStart;
      if (periodEnd) params.period_end = periodEnd;
      
      // Usa api (axios) con responseType blob
      const response = await api.get('/admin/payments/export/csv', { 
        params,
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      
      // Crea link e forza download
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `${exportType}_export_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
      
      toast.success('Download completato');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Errore durante l\'export');
    } finally {
      setExporting(false);
    }
  };

  const handleHistoryExport = async (includeOrders = false) => {
    setExporting(true);
    try {
      const params = { include_orders: includeOrders };
      if (periodStart) params.period_start = periodStart;
      if (periodEnd) params.period_end = periodEnd;
      
      // Usa api (axios) con responseType blob
      const response = await api.get('/admin/payments/vendors/history/export', { 
        params,
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      
      const filename = includeOrders 
        ? `storico_venditori_dettaglio_${new Date().toISOString().slice(0,10)}.csv`
        : `storico_payout_venditori_${new Date().toISOString().slice(0,10)}.csv`;
      
      // Crea link e forza download
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
      
      toast.success('Download completato');
    } catch (error) {
      console.error('Error exporting history:', error);
      toast.error('Errore durante l\'export');
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (value) => {
    return `€${(value || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-primary" />
            Analytics & Audit
          </h1>
          <p className="text-text-muted mt-1">
            Dashboard strategica - Solo visualizzazione
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Period Filters */}
          <div className="flex items-center gap-2">
            <Input 
              type="text"
              placeholder="Da (DD/MM/YYYY)"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
              className="w-[140px]"
            />
            <span className="text-text-muted">-</span>
            <Input 
              type="text"
              placeholder="A (DD/MM/YYYY)"
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
              className="w-[140px]"
            />
          </div>
          
          {/* Export Buttons */}
          <div className="flex items-center gap-2">
            <Select value="" onValueChange={handleExport}>
              <SelectTrigger className="w-[150px]" disabled={exporting}>
                {exporting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Export CSV
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="payouts">Storico Payout</SelectItem>
                <SelectItem value="orders">Ordini</SelectItem>
                <SelectItem value="commissions">Commissioni</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="kpi" className="gap-2">
            <TrendingUp className="w-4 h-4" />
            KPI Globali
          </TabsTrigger>
          <TabsTrigger value="breakdown" className="gap-2">
            <PieChart className="w-4 h-4" />
            Breakdown
          </TabsTrigger>
          <TabsTrigger value="subscriptions" className="gap-2">
            <Crown className="w-4 h-4" />
            Abbonamenti Pro
            {subscriptionsSummary?.total_active > 0 && (
              <Badge className="ml-1 bg-accent">{subscriptionsSummary.total_active}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <History className="w-4 h-4" />
            Storico Venditori
          </TabsTrigger>
          <TabsTrigger value="audit" className="gap-2">
            <AlertTriangle className="w-4 h-4" />
            Audit & Alert
            {audit?.summary?.total_alerts > 0 && (
              <Badge variant="destructive" className="ml-1">{audit.summary.total_alerts}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="disputes" className="gap-2">
            <ShieldAlert className="w-4 h-4" />
            Disputes
            {disputes.filter(d => !['won','lost','charge_refunded'].includes(d.status)).length > 0 && (
              <Badge variant="destructive" className="ml-1">
                {disputes.filter(d => !['won','lost','charge_refunded'].includes(d.status)).length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="ledger" className="gap-2">
            <CircleDollarSign className="w-4 h-4" />
            Ledger Vendor
          </TabsTrigger>
        </TabsList>

        {/* KPI Tab */}
        <TabsContent value="kpi">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : kpi ? (
            <div className="space-y-6">
              {/* Period Info */}
              <div className="text-sm text-text-muted">
                Periodo: {kpi.period?.start} - {kpi.period?.end}
              </div>

              {/* Main KPIs */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="border-l-4 border-l-blue-500">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-text-muted">GMV Totale</p>
                        <p className="text-2xl font-bold">{formatCurrency(kpi.kpi?.gmv)}</p>
                      </div>
                      <div className="p-3 bg-blue-100 rounded-xl">
                        <TrendingUp className="w-6 h-6 text-blue-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-primary">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-text-muted">Revenue DalSud</p>
                        <p className="text-2xl font-bold text-primary">{formatCurrency(kpi.kpi?.dalsud_revenue)}</p>
                        <p className="text-xs text-text-muted">Commissioni + IVA</p>
                      </div>
                      <div className="p-3 bg-primary/10 rounded-xl">
                        <CircleDollarSign className="w-6 h-6 text-primary" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-green-500">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-text-muted">Pagato ai Venditori</p>
                        <p className="text-2xl font-bold text-green-600">{formatCurrency(kpi.kpi?.net_paid_to_vendors)}</p>
                      </div>
                      <div className="p-3 bg-green-100 rounded-xl">
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-yellow-500">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-text-muted">Payout Pendenti</p>
                        <p className="text-2xl font-bold text-yellow-600">{formatCurrency(kpi.kpi?.pending_payout)}</p>
                      </div>
                      <div className="p-3 bg-yellow-100 rounded-xl">
                        <Clock className="w-6 h-6 text-yellow-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Detailed Breakdown */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Dettaglio Economico Venditori</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between py-2 border-b">
                        <span className="text-text-muted">GMV Totale</span>
                        <span className="font-semibold">{formatCurrency(kpi.kpi?.gmv)}</span>
                      </div>
                      <div className="flex items-center justify-between py-2 border-b">
                        <span className="text-text-muted">Commissioni DalSud</span>
                        <span className="font-semibold text-red-600">-{formatCurrency(kpi.kpi?.commission_total)}</span>
                      </div>
                      <div className="flex items-center justify-between py-2 bg-green-50 rounded px-2 border-b">
                        <span className="font-medium">Netto Venditori (no IVA)</span>
                        <span className="font-bold text-green-600">{formatCurrency(kpi.kpi?.net_no_vat)}</span>
                      </div>
                      <div className="flex items-center justify-between py-2 border-b opacity-70">
                        <span className="text-text-muted">IVA su Commissioni (22%)</span>
                        <span className="font-semibold text-orange-600">-{formatCurrency(kpi.kpi?.vat_total)}</span>
                      </div>
                      <div className="flex items-center justify-between py-2 opacity-70">
                        <span className="text-text-muted">Netto (se regime IVA)</span>
                        <span className="font-semibold">{formatCurrency(kpi.kpi?.net_with_vat)}</span>
                      </div>
                    </div>
                    <div className="mt-4 p-2 bg-blue-50 rounded-lg">
                      <p className="text-xs text-blue-700">
                        <strong>Forfettario:</strong> Netto (no IVA). <strong>Regime IVA:</strong> Netto (con IVA).
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Ordini per Stato Pagamento</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(kpi.orders?.by_payment_status || {}).map(([status, count]) => (
                        <div key={status} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {status === 'idoneo_payout' && <CheckCircle className="w-4 h-4 text-green-500" />}
                            {status === 'in_attesa' && <Clock className="w-4 h-4 text-yellow-500" />}
                            {status === 'bloccato' && <Ban className="w-4 h-4 text-red-500" />}
                            {status === 'pagato' && <CircleDollarSign className="w-4 h-4 text-blue-500" />}
                            <span className="capitalize">{status.replace('_', ' ')}</span>
                          </div>
                          <Badge variant="outline">{count}</Badge>
                        </div>
                      ))}
                      <div className="flex items-center justify-between pt-2 border-t">
                        <span className="font-medium">Totale Ordini</span>
                        <Badge>{kpi.orders?.total}</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          ) : null}
        </TabsContent>

        {/* Breakdown Tab */}
        <TabsContent value="breakdown">
          <div className="mb-4">
            <Select value={groupBy} onValueChange={setGroupBy}>
              <SelectTrigger className="w-[200px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Raggruppa per..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="vendor">Per Venditore</SelectItem>
                <SelectItem value="status">Per Stato Pagamento</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-3 font-medium">
                          {groupBy === 'vendor' ? 'Venditore' : 'Stato'}
                        </th>
                        <th className="pb-3 font-medium text-center">Ordini</th>
                        <th className="pb-3 font-medium text-right">GMV</th>
                        <th className="pb-3 font-medium text-right">Commissioni</th>
                        <th className="pb-3 font-medium text-right bg-green-50">Netto (no IVA)</th>
                        <th className="pb-3 font-medium text-right opacity-70">IVA</th>
                      </tr>
                    </thead>
                    <tbody>
                      {breakdown.map((item, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="py-3">
                            {groupBy === 'vendor' ? (
                              <div>
                                <p className="font-medium">{item.producer_name}</p>
                                <p className="text-xs text-text-muted">Comm: {((item.commission_rate || 0.10) * 100).toFixed(0)}%</p>
                              </div>
                            ) : (
                              <Badge variant="outline">{item.status_display?.label || item.status}</Badge>
                            )}
                          </td>
                          <td className="py-3 text-center">{item.orders_count}</td>
                          <td className="py-3 text-right">{formatCurrency(item.gmv)}</td>
                          <td className="py-3 text-right text-primary">{formatCurrency(item.commission)}</td>
                          <td className="py-3 text-right font-semibold text-green-600 bg-green-50">
                            {formatCurrency(item.net_no_vat || (item.gmv - item.commission))}
                          </td>
                          <td className="py-3 text-right opacity-70">{formatCurrency(item.vat)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-50 font-semibold">
                        <td className="py-3">Totale</td>
                        <td className="py-3 text-center">{breakdown.reduce((sum, i) => sum + i.orders_count, 0)}</td>
                        <td className="py-3 text-right">{formatCurrency(breakdown.reduce((sum, i) => sum + i.gmv, 0))}</td>
                        <td className="py-3 text-right text-primary">{formatCurrency(breakdown.reduce((sum, i) => sum + i.commission, 0))}</td>
                        <td className="py-3 text-right text-green-600 bg-green-50">
                          {formatCurrency(breakdown.reduce((sum, i) => sum + (i.net_no_vat || (i.gmv - i.commission)), 0))}
                        </td>
                        <td className="py-3 text-right opacity-70">{formatCurrency(breakdown.reduce((sum, i) => sum + i.vat, 0))}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-700">
                    <strong>Regime forfettario:</strong> Netto (no IVA) = GMV - Commissioni. 
                    <strong> Regime IVA:</strong> Sottrarre anche l'IVA per il netto finale.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Audit Tab */}
        <TabsContent value="audit">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : audit ? (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className={audit.summary?.overdue_producers > 0 ? 'border-red-200 bg-red-50' : ''}>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className={`w-8 h-8 ${audit.summary?.overdue_producers > 0 ? 'text-red-600' : 'text-gray-400'}`} />
                      <div>
                        <p className="text-sm text-text-muted">Venditori con Payout in Ritardo</p>
                        <p className="text-2xl font-bold">{audit.summary?.overdue_producers}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className={audit.summary?.blocked_orders > 0 ? 'border-yellow-200 bg-yellow-50' : ''}>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <Ban className={`w-8 h-8 ${audit.summary?.blocked_orders > 0 ? 'text-yellow-600' : 'text-gray-400'}`} />
                      <div>
                        <p className="text-sm text-text-muted">Ordini Bloccati {'>'} {audit.threshold_days}gg</p>
                        <p className="text-2xl font-bold">{audit.summary?.blocked_orders}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="w-8 h-8 text-blue-500" />
                      <div>
                        <p className="text-sm text-text-muted">Alert Totali</p>
                        <p className="text-2xl font-bold">{audit.summary?.total_alerts}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Alerts List */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Alert Attivi
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {audit.alerts?.length === 0 ? (
                    <div className="text-center py-8 text-text-muted">
                      <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                      <p>Nessun alert attivo - Tutto in ordine!</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {audit.alerts?.map((alert, idx) => (
                        <div 
                          key={idx}
                          className={`border rounded-lg p-4 ${
                            alert.severity === 'high' ? 'border-red-300 bg-red-50' : 
                            alert.severity === 'medium' ? 'border-yellow-300 bg-yellow-50' : 
                            'border-gray-200'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              {alert.severity === 'high' ? (
                                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                              ) : (
                                <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                              )}
                              <div>
                                <p className="font-medium">
                                  {alert.type === 'PAYOUT_OVERDUE' ? alert.producer_name : alert.order_number}
                                </p>
                                <p className="text-sm text-text-muted">{alert.message}</p>
                                
                                {alert.type === 'PAYOUT_OVERDUE' && (
                                  <div className="mt-2">
                                    <p className="text-sm font-medium text-red-700">
                                      Totale pendente: {formatCurrency(alert.total_pending)}
                                    </p>
                                    <div className="text-xs text-text-muted mt-1">
                                      {alert.orders?.slice(0, 3).map((o, i) => (
                                        <span key={i}>
                                          {o.order_number} ({o.days_overdue}gg)
                                          {i < Math.min(alert.orders.length, 3) - 1 ? ', ' : ''}
                                        </span>
                                      ))}
                                      {alert.orders?.length > 3 && ` +${alert.orders.length - 3} altri`}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                            <Badge 
                              variant={alert.severity === 'high' ? 'destructive' : 'outline'}
                              className={alert.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' : ''}
                            >
                              {alert.severity === 'high' ? 'Alta priorità' : 'Media priorità'}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Sezione: Salute Ledger */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CircleDollarSign className="w-5 h-5 text-primary" />
                    Salute Ledger Vendor
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {ledgerHealthLoading ? (
                    <div className="flex items-center gap-2 text-sm text-text-muted py-4">
                      <Loader2 className="w-4 h-4 animate-spin" /> Caricamento...
                    </div>
                  ) : ledgerHealth?.vendors?.length === 0 ? (
                    <div className="text-center py-6 text-text-muted">
                      <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-500" />
                      <p className="text-sm">Nessun vendor con saldo critico</p>
                    </div>
                  ) : ledgerHealth?.vendors?.length > 0 ? (
                    <div className="space-y-2">
                      {ledgerHealth.vendors.map(v => (
                        <div
                          key={v.producer_id}
                          className="flex items-center justify-between border border-red-200 bg-red-50 rounded-lg px-4 py-3"
                        >
                          <div>
                            <p className="font-medium text-sm">{v.producer_name}</p>
                            <p className="text-xs text-text-muted">
                              Disponibile: <span className={v.available_balance < 0 ? 'text-red-600 font-semibold' : ''}>{v.available_balance < 0 ? '-' : ''}€{Math.abs(v.available_balance).toFixed(2)}</span>
                              {v.hold_balance < 0 && (
                                <> · Hold: <span className="text-yellow-700 font-semibold">−€{Math.abs(v.hold_balance).toFixed(2)}</span></>
                              )}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-red-700 font-bold text-sm">
                              €{v.total_balance.toFixed(2)}
                            </span>
                            <button
                              onClick={() => goToLedgerTab(v.producer_id, v.producer_name)}
                              className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                            >
                              <CircleDollarSign className="w-3 h-3" />
                              Ledger
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-text-muted py-4">Carica il tab per vedere lo stato ledger.</p>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : null}
        </TabsContent>

        {/* Storico Venditori Tab */}
        <TabsContent value="history">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : vendorsHistory ? (
            <div className="space-y-6">
              {/* Period Info & Export */}
              <div className="flex items-center justify-between">
                <div className="text-sm text-text-muted">
                  Periodo: {vendorsHistory.period?.start} - {vendorsHistory.period?.end}
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleHistoryExport(false)}
                    disabled={exporting}
                    className="gap-2"
                  >
                    {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    Export Payout
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleHistoryExport(true)}
                    disabled={exporting}
                    className="gap-2"
                  >
                    {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
                    Export con Ordini
                  </Button>
                </div>
              </div>

              {/* Grand Totals */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-xs text-text-muted">Venditori</p>
                    <p className="text-xl font-bold">{vendorsHistory.grand_totals?.vendors_count}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-xs text-text-muted">Totale Lordo</p>
                    <p className="text-xl font-bold">{formatCurrency(vendorsHistory.grand_totals?.gross_total)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-xs text-text-muted">Commissioni</p>
                    <p className="text-xl font-bold text-primary">{formatCurrency(vendorsHistory.grand_totals?.commission_total)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-xs text-text-muted">IVA</p>
                    <p className="text-xl font-bold text-orange-600">{formatCurrency(vendorsHistory.grand_totals?.vat_total)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-green-50">
                  <CardContent className="pt-4">
                    <p className="text-xs text-text-muted">Netto Pagato</p>
                    <p className="text-xl font-bold text-green-600">{formatCurrency(vendorsHistory.grand_totals?.net_total)}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Vendors List */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Storico per Venditore
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {vendorsHistory.vendors?.length === 0 ? (
                    <div className="text-center py-8 text-text-muted">
                      <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Nessun dato storico disponibile</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {vendorsHistory.vendors?.map((vendor) => (
                        <div 
                          key={vendor.producer_id}
                          className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div>
                              <p className="font-medium text-lg">{vendor.producer_name}</p>
                              <p className="text-sm text-text-muted">{vendor.producer_email}</p>
                              <p className="text-xs text-text-muted mt-1">
                                Commissione: {((vendor.commission_rate || 0.10) * 100).toFixed(0)}%
                              </p>
                            </div>
                            
                            <div className="flex flex-wrap items-center gap-4">
                              {/* Ordini per stato */}
                              <div className="flex items-center gap-2 text-xs">
                                <span className="text-yellow-600">In attesa: {vendor.orders_by_status?.in_attesa || 0}</span>
                                <span className="text-green-600">Idonei: {vendor.orders_by_status?.idoneo_payout || 0}</span>
                                <span className="text-red-600">Bloccati: {vendor.orders_by_status?.bloccato || 0}</span>
                                <span className="text-blue-600">Pagati: {vendor.orders_by_status?.pagato || 0}</span>
                              </div>
                            </div>
                            
                            <div className="flex flex-wrap items-center gap-3">
                              <div className="text-right">
                                <p className="text-xs text-text-muted" title="Somma lordo di tutti gli ordini (pagati + in attesa + bloccati)">Lordo Totale</p>
                                <p className="font-semibold">{formatCurrency(vendor.totals?.gross_total)}</p>
                                {vendor.totals?.by_status?.pagato?.gross > 0 && (
                                  <p className="text-xs text-gray-400">di cui pagato: {formatCurrency(vendor.totals.by_status.pagato.gross)}</p>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-text-muted">Commissioni</p>
                                <p className="font-semibold text-primary">{formatCurrency(vendor.totals?.commission_total)}</p>
                              </div>
                              <div className="text-right bg-green-50 rounded px-2 py-1">
                                <p className="text-xs text-text-muted" title="Lordo Totale - Commissioni (include tutti gli stati)">Netto Totale</p>
                                <p className="font-bold text-green-600">{formatCurrency(vendor.totals?.net_total)}</p>
                              </div>
                              {vendor.totals?.shipping_refunded > 0 && (
                                <div className="text-right">
                                  <p className="text-xs text-text-muted">Sped. Pagate</p>
                                  <p className="font-semibold text-blue-600">+{formatCurrency(vendor.totals?.shipping_refunded)}</p>
                                </div>
                              )}
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  setSelectedVendor(vendor);
                                  fetchVendorOrders(vendor.producer_id);
                                }}
                                className="gap-1"
                              >
                                <FileText className="w-4 h-4" />
                                Ordini
                              </Button>
                            </div>
                          </div>
                          
                          {/* Storico Payout */}
                          {vendor.payouts_history?.length > 0 && (
                            <div className="mt-4 pt-4 border-t">
                              <p className="text-sm font-medium mb-2">Storico Payout ({vendor.totals?.payouts_count} pagamenti)</p>
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="text-left text-text-muted">
                                      <th className="pb-2">Periodo</th>
                                      <th className="pb-2 text-right">Lordo</th>
                                      <th className="pb-2 text-right">Comm.</th>
                                      <th className="pb-2 text-right" title="Netto prodotti + spedizione rimborsata">Tot. Pagato</th>
                                      <th className="pb-2 text-right">Ordini</th>
                                      <th className="pb-2">Rif.</th>
                                      <th className="pb-2">Data</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {vendor.payouts_history.slice(0, 5).map((payout) => (
                                      <tr key={payout.id} className="border-t">
                                        <td className="py-2">{payout.period}</td>
                                        <td className="py-2 text-right">{formatCurrency(payout.gross_amount)}</td>
                                        <td className="py-2 text-right text-primary">{formatCurrency(payout.commission)}</td>
                                        <td className="py-2 text-right font-semibold text-green-600">{formatCurrency(payout.net_amount)}</td>
                                        <td className="py-2 text-right">{payout.orders_count}</td>
                                        <td className="py-2 font-mono text-xs">{payout.payment_reference}</td>
                                        <td className="py-2">{payout.processed_at}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                {vendor.payouts_history.length > 5 && (
                                  <p className="text-xs text-text-muted mt-2">
                                    + altri {vendor.payouts_history.length - 5} pagamenti...
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Vendor Orders Modal */}
              {selectedVendor && vendorOrders && (
                <Card className="border-2 border-primary">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg">
                      Ordini - {selectedVendor.producer_name}
                    </CardTitle>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        setSelectedVendor(null);
                        setVendorOrders(null);
                      }}
                    >
                      Chiudi
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm text-text-muted mb-4">
                      {vendorOrders.pagination?.total} ordini trovati
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left border-b">
                            <th className="pb-2">Ordine</th>
                            <th className="pb-2">Data</th>
                            <th className="pb-2">Stato Pag.</th>
                            <th className="pb-2 text-right">Lordo</th>
                            <th className="pb-2 text-right">Comm.</th>
                            <th className="pb-2 text-right">Netto</th>
                            <th className="pb-2 text-right">Sped.</th>
                            <th className="pb-2 text-right bg-green-50">Tot. Payout</th>
                            <th className="pb-2 text-center">Reso</th>
                            <th className="pb-2 text-right">Rimborso</th>
                          </tr>
                        </thead>
                        <tbody>
                          {vendorOrders.orders?.map((order) => (
                            <tr key={order.id} className={`border-b hover:bg-gray-50 ${order.active_dispute ? 'bg-red-50/40' : order.return_info ? 'bg-orange-50/30' : ''}`}>
                              <td className="py-2 font-mono text-xs">
                                <div className="flex items-center gap-1.5">
                                  {order.order_number}
                                  {order.active_dispute && (
                                    <span title={`Chargeback: ${order.active_dispute.reason?.replace(/_/g,' ')} — scadenza ${order.active_dispute.due_by ? new Date(order.active_dispute.due_by).toLocaleDateString('it-IT') : 'n/d'}`} className="text-red-500 cursor-help">⚠️</span>
                                  )}
                                </div>
                              </td>
                              <td className="py-2">{order.created_at ? new Date(order.created_at).toLocaleDateString('it-IT') : '-'}</td>
                              <td className="py-2">
                                <div className="flex flex-col gap-0.5">
                                  <Badge
                                    variant="outline"
                                    className={`
                                      ${order.payment_status === 'idoneo_payout' ? 'bg-green-100 text-green-700' : ''}
                                      ${order.payment_status === 'in_attesa' ? 'bg-yellow-100 text-yellow-700' : ''}
                                      ${order.payment_status === 'bloccato' ? 'bg-red-100 text-red-700' : ''}
                                      ${order.payment_status === 'pagato' ? 'bg-blue-100 text-blue-700' : ''}
                                    `}
                                  >
                                    {order.payment_status_display?.label || order.payment_status}
                                  </Badge>
                                  {order.active_dispute && (
                                    <span className="text-[10px] text-red-600 font-medium">
                                      Chargeback {order.active_dispute.reason?.replace(/_/g,' ')}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="py-2 text-right">{formatCurrency(order.economics?.vendor_gross)}</td>
                              <td className="py-2 text-right text-primary">{formatCurrency(order.economics?.commission_amount)}</td>
                              <td className="py-2 text-right">{formatCurrency(order.economics?.vendor_net)}</td>
                              <td className="py-2 text-right text-blue-600">+{formatCurrency(order.economics?.shipping_share)}</td>
                              <td className="py-2 text-right font-bold text-green-600 bg-green-50">
                                {formatCurrency(order.economics?.net_payout ?? order.economics?.total_payout)}
                                {order.economics?.refund_deducted > 0 && (
                                  <div className="text-[10px] text-orange-500 font-normal">−€{order.economics?.refund_deducted?.toFixed(2)} resi</div>
                                )}
                              </td>
                              {/* Colonna Reso */}
                              <td className="py-2 text-center">
                                {order.return_info ? (
                                  <Badge className={
                                    order.return_info.status === 'return_completed' ? 'bg-green-100 text-green-800 text-[10px]' :
                                    order.return_info.status === 'return_rejected' ? 'bg-gray-100 text-gray-600 text-[10px]' :
                                    'bg-orange-100 text-orange-800 text-[10px]'
                                  }>
                                    <RotateCcw className="w-3 h-3 mr-0.5 inline" />
                                    {order.return_info.status === 'return_completed' ? 'Completato' :
                                     order.return_info.status === 'return_rejected' ? 'Rifiutato' :
                                     order.return_info.status === 'return_requested' ? 'Richiesto' :
                                     order.return_info.status === 'return_approved' ? 'Approvato' :
                                     order.return_info.status === 'return_in_transit' ? 'In transito' :
                                     order.return_info.status === 'return_received' ? 'Ricevuto' :
                                     order.return_info.status}
                                  </Badge>
                                ) : (
                                  <span className="text-text-muted text-xs">—</span>
                                )}
                              </td>
                              {/* Colonna Rimborso */}
                              <td className="py-2 text-right text-xs">
                                {order.refund_info ? (
                                  <div>
                                    <span className={`font-semibold ${
                                      order.refund_info.status === 'succeeded' ? 'text-green-600' :
                                      order.refund_info.status === 'failed' ? 'text-red-600' :
                                      order.refund_info.status === 'skipped' ? 'text-gray-500' :
                                      'text-yellow-600'
                                    }`}>
                                      {order.refund_info.status === 'skipped'
                                        ? `⚠ saltato`
                                        : order.refund_info.status === 'failed'
                                        ? `✗ fallito`
                                        : `−€${order.refund_info.amount?.toFixed(2)}`}
                                    </span>
                                    {(order.refund_info.status === 'skipped' || order.refund_info.status === 'failed') && (
                                      <p className="text-[10px] text-text-muted leading-tight">
                                        {order.refund_info.failure_reason || order.refund_info.notes || 'verifica manuale'}
                                      </p>
                                    )}
                                  </div>
                                ) : order.return_info ? (
                                  <span className="text-text-muted">n/d</span>
                                ) : (
                                  <span className="text-text-muted">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : null}
        </TabsContent>

        {/* Subscriptions Tab */}
        <TabsContent value="subscriptions">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Summary Cards */}
              {subscriptionsSummary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-text-muted">Abbonati Attivi</p>
                          <p className="text-3xl font-bold text-accent">{subscriptionsSummary.total_active || 0}</p>
                        </div>
                        <Crown className="w-8 h-8 text-accent opacity-50" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-text-muted">Disdetti</p>
                          <p className="text-3xl font-bold text-red-500">{subscriptionsSummary.total_canceled || 0}</p>
                        </div>
                        <Ban className="w-8 h-8 text-red-500 opacity-50" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-text-muted">Revenue Mensile</p>
                          <p className="text-3xl font-bold text-green-600">
                            €{((subscriptionsSummary.total_active || 0) * 29.90).toFixed(2)}
                          </p>
                        </div>
                        <Euro className="w-8 h-8 text-green-600 opacity-50" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-text-muted">Pagamenti Totali</p>
                          <p className="text-3xl font-bold text-blue-600">{subscriptionsSummary.total_payments_count || 0}</p>
                        </div>
                        <CircleDollarSign className="w-8 h-8 text-blue-600 opacity-50" />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Status Breakdown */}
              {subscriptionsSummary?.by_status && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Distribuzione per Stato</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-4">
                      {Object.entries(subscriptionsSummary.by_status).map(([status, count]) => (
                        <div key={status} className="flex items-center gap-2">
                          <Badge className={`
                            ${status === 'active' ? 'bg-green-100 text-green-800' : ''}
                            ${status === 'canceled' ? 'bg-red-100 text-red-800' : ''}
                            ${status === 'incomplete' ? 'bg-yellow-100 text-yellow-800' : ''}
                            ${status === 'past_due' ? 'bg-orange-100 text-orange-800' : ''}
                            ${status === 'unpaid' ? 'bg-gray-100 text-gray-800' : ''}
                            ${status === 'trialing' ? 'bg-blue-100 text-blue-800' : ''}
                          `}>
                            {status === 'active' && 'Attivo'}
                            {status === 'canceled' && 'Disdetto'}
                            {status === 'incomplete' && 'Incompleto'}
                            {status === 'past_due' && 'Scaduto'}
                            {status === 'unpaid' && 'Non Pagato'}
                            {status === 'trialing' && 'Prova'}
                            {!['active', 'canceled', 'incomplete', 'past_due', 'unpaid', 'trialing'].includes(status) && status}
                          </Badge>
                          <span className="font-bold">{count}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Subscriptions List */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Lista Abbonamenti</CardTitle>
                </CardHeader>
                <CardContent>
                  {subscriptions?.items?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="py-3 px-4 text-left font-medium">Venditore</th>
                            <th className="py-3 px-4 text-left font-medium">Email</th>
                            <th className="py-3 px-4 text-center font-medium">Stato</th>
                            <th className="py-3 px-4 text-right font-medium">Importo</th>
                            <th className="py-3 px-4 text-left font-medium">Data Inizio</th>
                            <th className="py-3 px-4 text-left font-medium">Prossimo Rinnovo</th>
                            <th className="py-3 px-4 text-left font-medium">Ultimo Pagamento</th>
                          </tr>
                        </thead>
                        <tbody>
                          {subscriptions.items.map((sub) => (
                            <tr key={sub.id} className="border-b hover:bg-gray-50">
                              <td className="py-3 px-4 font-medium">{sub.producer_name || sub.billing_name}</td>
                              <td className="py-3 px-4 text-text-muted">{sub.producer_email || sub.billing_email}</td>
                              <td className="py-3 px-4 text-center">
                                <Badge className={`
                                  ${sub.status === 'active' ? 'bg-green-100 text-green-800' : ''}
                                  ${sub.status === 'canceled' ? 'bg-red-100 text-red-800' : ''}
                                  ${sub.status === 'incomplete' ? 'bg-yellow-100 text-yellow-800' : ''}
                                  ${sub.status === 'past_due' ? 'bg-orange-100 text-orange-800' : ''}
                                `}>
                                  {sub.status === 'active' && 'Attivo'}
                                  {sub.status === 'canceled' && 'Disdetto'}
                                  {sub.status === 'incomplete' && 'Incompleto'}
                                  {sub.status === 'past_due' && 'Scaduto'}
                                  {!['active', 'canceled', 'incomplete', 'past_due'].includes(sub.status) && sub.status}
                                </Badge>
                              </td>
                              <td className="py-3 px-4 text-right font-medium">€{sub.amount?.toFixed(2)}</td>
                              <td className="py-3 px-4">
                                {sub.start_date ? new Date(sub.start_date).toLocaleDateString('it-IT') : '-'}
                              </td>
                              <td className="py-3 px-4">
                                {sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString('it-IT') : '-'}
                              </td>
                              <td className="py-3 px-4">
                                {sub.last_payment_date ? (
                                  <span>
                                    {new Date(sub.last_payment_date).toLocaleDateString('it-IT')}
                                    {sub.last_payment_amount && (
                                      <span className="text-green-600 ml-2">€{sub.last_payment_amount.toFixed(2)}</span>
                                    )}
                                  </span>
                                ) : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-text-muted">
                      <Crown className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>Nessun abbonamento registrato</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
        {/* Disputes Tab */}
        <TabsContent value="disputes">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-destructive" />
              Dispute Stripe (Chargeback)
            </h2>
            <Button variant="outline" size="sm" onClick={fetchDisputes} disabled={disputesLoading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${disputesLoading ? 'animate-spin' : ''}`} />
              Aggiorna
            </Button>
          </div>

          {disputesLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : disputes.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center text-text-muted">
                <ShieldAlert className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">Nessuna disputa registrata</p>
                <p className="text-sm mt-1">Le dispute Stripe appariranno qui quando verranno create via webhook.</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="text-left py-3 px-4 font-medium">Ordine</th>
                        <th className="text-left py-3 px-4 font-medium">Cliente</th>
                        <th className="text-left py-3 px-4 font-medium">Prodotti</th>
                        <th className="text-right py-3 px-4 font-medium">Importo</th>
                        <th className="text-left py-3 px-4 font-medium">Motivo</th>
                        <th className="text-left py-3 px-4 font-medium">Status</th>
                        <th className="text-left py-3 px-4 font-medium">Scadenza</th>
                        <th className="text-center py-3 px-4 font-medium">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {disputes.map((d) => {
                        const isClosed = ['won', 'lost', 'charge_refunded'].includes(d.status);
                        const dueDate = d.due_by ? new Date(d.due_by) : null;
                        const daysLeft = dueDate ? Math.ceil((dueDate - Date.now()) / 86400000) : null;
                        const isUrgent = daysLeft !== null && daysLeft <= 3 && !isClosed;
                        return (
                          <tr key={d.id} className={`border-b hover:bg-muted/20 transition-colors ${isUrgent ? 'bg-red-50/40' : ''}`}>
                            <td className="py-3 px-4">
                              {d.order_number ? (
                                <div>
                                  <p className="font-mono text-xs font-medium text-primary">{d.order_number}</p>
                                  <p className="text-xs text-text-muted">{d.order_status}</p>
                                </div>
                              ) : (
                                <div>
                                  <p className="text-xs text-text-muted italic">non collegato</p>
                                  <p className="font-mono text-xs text-text-muted">{d.stripe_charge_id?.slice(0, 12)}…</p>
                                </div>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              {d.customer_name ? (
                                <div>
                                  <p className="text-xs font-medium">{d.customer_name}</p>
                                  <p className="text-xs text-text-muted">{d.customer_email}</p>
                                </div>
                              ) : '—'}
                            </td>
                            <td className="py-3 px-4 text-xs text-text-muted max-w-[160px]">
                              {d.products?.length > 0
                                ? d.products.join(', ')
                                : '—'}
                            </td>
                            <td className="py-3 px-4 text-right font-medium">
                              {d.amount != null ? `€${d.amount.toFixed(2)}` : '—'}
                            </td>
                            <td className="py-3 px-4">
                              {d.reason ? (
                                <Badge variant="outline" className="text-xs">
                                  {d.reason.replace(/_/g, ' ')}
                                </Badge>
                              ) : '—'}
                            </td>
                            <td className="py-3 px-4">
                              <Badge
                                variant={
                                  d.status === 'won' ? 'success' :
                                  d.status === 'lost' ? 'destructive' :
                                  d.status === 'needs_response' ? 'warning' :
                                  'secondary'
                                }
                                className="text-xs capitalize"
                              >
                                {d.status?.replace(/_/g, ' ') || '—'}
                              </Badge>
                            </td>
                            <td className="py-3 px-4">
                              {dueDate ? (
                                <div>
                                  <p className={`text-xs font-medium ${isUrgent ? 'text-red-600' : ''}`}>
                                    {dueDate.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                  </p>
                                  {!isClosed && daysLeft !== null && (
                                    <p className={`text-xs ${daysLeft <= 3 ? 'text-red-600 font-semibold' : 'text-text-muted'}`}>
                                      {daysLeft > 0 ? `${daysLeft}gg` : 'Scaduta'}
                                    </p>
                                  )}
                                </div>
                              ) : '—'}
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-1.5 justify-center">
                                <Button
                                  size="sm"
                                  variant={isClosed ? 'ghost' : 'outline'}
                                  disabled={isClosed}
                                  onClick={() => openEvidenceModal(d)}
                                  className="gap-1 text-xs"
                                >
                                  <Send className="w-3 h-3" />
                                  Evidence
                                </Button>
                                {d.vendors?.length > 0 && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => goToLedgerTab(d.vendors[0].producer_id, d.vendors[0].producer_name)}
                                    className="gap-1 text-xs text-blue-600 hover:text-blue-700"
                                    title="Vedi ledger vendor"
                                  >
                                    <CircleDollarSign className="w-3 h-3" />
                                    Ledger
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Ledger Vendor Tab */}
        <TabsContent value="ledger">
          <div className="space-y-5">
            {/* Selettore produttore */}
            <div className="bg-white border border-border rounded-xl p-5">
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <CircleDollarSign className="w-4 h-4 text-primary" />
                Seleziona Produttore
              </h3>
              {ledgerProducersLoading ? (
                <div className="flex items-center gap-2 text-sm text-text-muted">
                  <Loader2 className="w-4 h-4 animate-spin" /> Caricamento produttori...
                </div>
              ) : (
                <select
                  value={ledgerProducerId}
                  onChange={e => {
                    const selected = ledgerProducers.find(p => p.id === e.target.value);
                    setLedgerProducerId(e.target.value);
                    setLedgerProducerName(selected?.name || '');
                  }}
                  className="text-sm border border-border rounded-lg px-3 py-2 bg-white w-full max-w-md focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  <option value="">— Seleziona un produttore —</option>
                  {ledgerProducers.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Panel finanziario */}
            <AdminVendorFinancePanel
              producerId={ledgerProducerId}
              producerName={ledgerProducerName}
            />
          </div>
        </TabsContent>
      </Tabs>

      {/* Evidence Modal */}
      {disputeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-background rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Send className="w-5 h-5 text-primary" />
                  Submit Evidence
                </h3>
                <p className="text-xs text-text-muted mt-0.5 font-mono">
                  {disputeModal.stripe_dispute_id}
                </p>
              </div>
              <button
                onClick={closeEvidenceModal}
                className="p-1 rounded hover:bg-muted transition-colors"
                disabled={evidenceSubmitting}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-text-muted">
                I campi compilati verranno combinati con i dati automatici dell'ordine e inviati a Stripe.
                Lascia vuoti i campi già disponibili nel sistema.
              </p>

              {disputeModal.order_number ? (
                <div className="text-xs bg-muted rounded p-2 space-y-0.5">
                  <div><span className="text-text-muted">Ordine:</span> <span className="font-mono font-medium text-primary">{disputeModal.order_number}</span></div>
                  {disputeModal.customer_name && <div><span className="text-text-muted">Cliente:</span> {disputeModal.customer_name} — {disputeModal.customer_email}</div>}
                  {disputeModal.products?.length > 0 && <div><span className="text-text-muted">Prodotti:</span> {disputeModal.products.join(', ')}</div>}
                  {disputeModal.due_by && <div><span className="text-text-muted">Scadenza:</span> <span className="font-medium">{new Date(disputeModal.due_by).toLocaleDateString('it-IT')}</span></div>}
                </div>
              ) : disputeModal.order_id && (
                <div className="text-xs bg-muted rounded p-2 font-mono">
                  Order ID: <span className="text-primary">{disputeModal.order_id}</span>
                </div>
              )}

              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium block mb-1">Comunicazione cliente</label>
                  <textarea
                    className="w-full border rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                    rows={3}
                    placeholder="Email, chat, o altri scambi con il cliente..."
                    value={evidenceForm.customer_communication}
                    onChange={e => setEvidenceForm(f => ({ ...f, customer_communication: e.target.value }))}
                    disabled={evidenceSubmitting}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Descrizione prodotto</label>
                  <Input
                    placeholder="Descrizione dei prodotti acquistati..."
                    value={evidenceForm.product_description}
                    onChange={e => setEvidenceForm(f => ({ ...f, product_description: e.target.value }))}
                    disabled={evidenceSubmitting}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium block mb-1">Corriere</label>
                    <Input
                      placeholder="es. GLS, DHL..."
                      value={evidenceForm.shipping_carrier}
                      onChange={e => setEvidenceForm(f => ({ ...f, shipping_carrier: e.target.value }))}
                      disabled={evidenceSubmitting}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1">Tracking number</label>
                    <Input
                      placeholder="Numero di tracciamento"
                      value={evidenceForm.shipping_tracking_number}
                      onChange={e => setEvidenceForm(f => ({ ...f, shipping_tracking_number: e.target.value }))}
                      disabled={evidenceSubmitting}
                    />
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 text-xs text-yellow-800">
                <strong>Attenzione:</strong> La submission è irreversibile. Stripe non permette modifiche dopo l'invio.
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 pb-6">
              <Button variant="outline" onClick={closeEvidenceModal} disabled={evidenceSubmitting}>
                Annulla
              </Button>
              <Button onClick={submitEvidence} disabled={evidenceSubmitting} className="gap-2">
                {evidenceSubmitting
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Send className="w-4 h-4" />
                }
                {evidenceSubmitting ? 'Invio in corso...' : 'Invia Evidence a Stripe'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminAnalytics;
